import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useSiteContent } from '../context/SiteContentContext';
import Button from '../common/Button';
import SafeIcon from '../common/SafeIcon';
import OptimizedImage from './OptimizedImage';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../lib/supabase';
import CartUpsell from './shop/CartUpsell';

const { FiX, FiShoppingBag, FiTrash2, FiPlus, FiMinus, FiArrowRight, FiPackage, FiTruck, FiCheckCircle, FiAlertCircle } = FiIcons;

// Toast Notification Component
const Toast = ({ message, type = 'success', onClose }) => {
    useEffect(() => {
        const timer = setTimeout(onClose, 3000);
        return () => clearTimeout(timer);
    }, [onClose]);

    return (
        <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-24 right-4 z-[80] px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 ${type === 'success'
                ? 'bg-emerald-500 text-white'
                : 'bg-red-500 text-white'
                }`}
        >
            <SafeIcon icon={type === 'success' ? FiCheckCircle : FiAlertCircle} className="w-5 h-5" />
            <span className="font-medium">{message}</span>
        </motion.div>
    );
};

const CartDrawer = () => {
    const navigate = useNavigate();
    const { isCartOpen, closeCart, cartItems, removeFromCart, updateQuantity, cartTotal } = useCart();
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState(null);
    const [removingItemId, setRemovingItemId] = useState(null);
    const { getSetting } = useSiteContent();

    const FREE_SHIPPING_THRESHOLD = parseFloat(getSetting('cart_free_shipping_threshold', 100));
    const cartAnnouncement = getSetting('cart_announcement', '');
    const emptyTitle = getSetting('cart_empty_title', 'Your cart is empty');
    const emptySubtitle = getSetting('cart_empty_subtitle', 'Add some amazing products to get started!');

    const shippingProgress = Math.min((cartTotal / FREE_SHIPPING_THRESHOLD) * 100, 100);
    const remainingForFreeShipping = Math.max(FREE_SHIPPING_THRESHOLD - cartTotal, 0);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const handleRemoveItem = (item) => {
        setRemovingItemId(item.id);
        setTimeout(() => {
            removeFromCart(item.id);
            setRemovingItemId(null);
            showToast(`${item.title} removed from cart`);
        }, 300);
    };

    const handleQuantityUpdate = (item, delta) => {
        updateQuantity(item.id, delta);
        if (delta > 0) {
            showToast('Quantity updated');
        }
    };

    const handleCheckout = () => {
        closeCart();
        navigate('/checkout');
    };

    return (
        <>
            <AnimatePresence>
                {toast && (
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast(null)}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {isCartOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={closeCart}
                            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60]"
                        />

                        {/* Drawer */}
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            className="fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-slate-900 shadow-2xl z-[70] flex flex-col border-l border-slate-200 dark:border-slate-800"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-3">
                                    <SafeIcon icon={FiShoppingBag} className="w-6 h-6 text-slate-900 dark:text-white" />
                                    <div>
                                        <h2 className="font-serif text-2xl font-bold text-slate-900 dark:text-white">Your Cart</h2>
                                        {cartItems.length > 0 && (
                                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                                {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={closeCart}
                                    className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
                                    aria-label="Close cart"
                                >
                                    <SafeIcon icon={FiX} className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Optional Announcement */}
                            {cartAnnouncement && (
                                <div className="bg-emerald-500 text-white px-6 py-2 text-center text-sm font-medium">
                                    {cartAnnouncement}
                                </div>
                            )}

                            {/* Free Shipping Progress */}
                            {cartItems.length > 0 && FREE_SHIPPING_THRESHOLD > 0 && cartTotal < FREE_SHIPPING_THRESHOLD && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="px-6 py-4 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-900/30"
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center space-x-2 text-sm">
                                            <SafeIcon icon={FiTruck} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                            <span className="font-medium text-blue-900 dark:text-blue-100">
                                                ${remainingForFreeShipping.toFixed(2)} away from free shipping!
                                            </span>
                                        </div>
                                    </div>
                                    <div className="w-full h-2 bg-blue-100 dark:bg-blue-900/40 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${shippingProgress}%` }}
                                            transition={{ duration: 0.5 }}
                                            className="h-full bg-blue-500 rounded-full"
                                        />
                                    </div>
                                </motion.div>
                            )}

                            {/* Items */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                {cartItems.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center text-center space-y-4 text-slate-400">
                                        <motion.div
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ type: 'spring', damping: 15 }}
                                        >
                                            <SafeIcon icon={FiShoppingBag} className="w-20 h-20 opacity-20" />
                                        </motion.div>
                                        <div>
                                            <p className="text-lg font-medium text-slate-600 dark:text-slate-400 mb-2">{emptyTitle}</p>
                                            <p className="text-sm text-slate-500">{emptySubtitle}</p>
                                        </div>
                                        <Button onClick={closeCart} variant="outline" className="mt-4">
                                            Continue Shopping
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        <AnimatePresence mode="popLayout">
                                            {cartItems.map((item) => (
                                                <motion.div
                                                    key={item.id}
                                                    layout
                                                    initial={{ opacity: 0, x: 20 }}
                                                    animate={{
                                                        opacity: removingItemId === item.id ? 0 : 1,
                                                        x: removingItemId === item.id ? -100 : 0,
                                                        scale: removingItemId === item.id ? 0.8 : 1
                                                    }}
                                                    exit={{ opacity: 0, x: -100, scale: 0.8 }}
                                                    transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                                                    className="flex gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl"
                                                >
                                                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700 flex-shrink-0">
                                                        <OptimizedImage
                                                            src={item.image}
                                                            alt={item.title}
                                                            type="thumbnail"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="font-bold text-slate-900 dark:text-white line-clamp-1 mb-1">
                                                            {item.title}
                                                        </h3>
                                                        {item.selectedTier && (
                                                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                                                                {item.selectedTier.charAt(0).toUpperCase() + item.selectedTier.slice(1)} Tier
                                                            </p>
                                                        )}
                                                        <p className="text-sm font-bold text-slate-900 dark:text-white mb-3">
                                                            {item.price === 0 ? 'Free' : `$${item.price}`}
                                                        </p>

                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center border-2 border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                                                                <motion.button
                                                                    whileTap={{ scale: 0.9 }}
                                                                    onClick={() => handleQuantityUpdate(item, -1)}
                                                                    className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                                                                    aria-label="Decrease quantity"
                                                                >
                                                                    <SafeIcon icon={FiMinus} className="w-4 h-4" />
                                                                </motion.button>
                                                                <span className="text-sm font-bold px-4 min-w-[3rem] text-center text-slate-900 dark:text-white">
                                                                    {item.quantity}
                                                                </span>
                                                                <motion.button
                                                                    whileTap={{ scale: 0.9 }}
                                                                    onClick={() => handleQuantityUpdate(item, 1)}
                                                                    className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                                                                    aria-label="Increase quantity"
                                                                >
                                                                    <SafeIcon icon={FiPlus} className="w-4 h-4" />
                                                                </motion.button>
                                                            </div>
                                                            <motion.button
                                                                whileHover={{ scale: 1.1 }}
                                                                whileTap={{ scale: 0.9 }}
                                                                onClick={() => handleRemoveItem(item)}
                                                                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                                                                aria-label="Remove item"
                                                            >
                                                                <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                                                            </motion.button>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            ))}
                                        </AnimatePresence>

                                        <CartUpsell />
                                    </>
                                )}
                            </div>

                            {/* Footer */}
                            {cartItems.length > 0 && (
                                <div className="p-6 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50">
                                    <div className="space-y-3 mb-6">
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-500 dark:text-slate-400">Subtotal</span>
                                            <span className="font-medium text-slate-900 dark:text-white">
                                                ${cartTotal.toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-500 dark:text-slate-400">Shipping</span>
                                            <span className="font-medium text-slate-900 dark:text-white">
                                                {cartTotal >= FREE_SHIPPING_THRESHOLD ? (
                                                    <span className="text-emerald-500 flex items-center space-x-1">
                                                        <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                                                        <span>Free</span>
                                                    </span>
                                                ) : (
                                                    'Calculated at checkout'
                                                )}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center pt-3 border-t border-slate-200 dark:border-slate-700">
                                            <span className="font-bold text-slate-900 dark:text-white">Total</span>
                                            <span className="font-bold text-2xl text-slate-900 dark:text-white">
                                                ${cartTotal.toFixed(2)}
                                            </span>
                                        </div>
                                    </div>

                                    <Button
                                        className="w-full justify-center py-4 text-lg"
                                        icon={isLoading ? undefined : FiArrowRight}
                                        variant="primary"
                                        onClick={handleCheckout}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <div className="flex items-center space-x-2">
                                                <motion.div
                                                    animate={{ rotate: 360 }}
                                                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                                >
                                                    <SafeIcon icon={FiPackage} className="w-5 h-5" />
                                                </motion.div>
                                                <span>Processing...</span>
                                            </div>
                                        ) : (
                                            'Checkout Now'
                                        )}
                                    </Button>
                                    <p className="text-xs text-center text-slate-500 dark:text-slate-400 mt-3">
                                        Secure checkout powered by Stripe
                                    </p>
                                </div>
                            )}
                        </motion.div>
                    </>
                )}
            </AnimatePresence >
        </>
    );
};

export default CartDrawer;
